import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import type {
  Bdkv2BookDescriptor,
  Bdkv2Value,
} from '@/generated/hooks/bdk/models';
import { captureException } from '@/modules/sentry';
import { validateOAuthRedirectUrl } from '@/shared/utils';
import {
  clearTriggerConnectionIntent,
  writeTriggerConnectionIntent,
  type TTriggerConnectionIntentKind,
} from '../utils/trigger-connection-intent';

/** `AddConnectionDialog`'s OAuth hand-off signature. */
export type TTriggerOAuthClick = (
  authMethodId: string,
  connectionName: string,
  credentials: Record<string, string>,
  labels: string[],
  configMap?: Record<string, Bdkv2Value>,
) => Promise<void>;

interface IUseTriggerOAuthConnectionParams {
  /** Which trigger dialog is hosting the connection wizard. */
  triggerKind: TTriggerConnectionIntentKind;
  /** Book descriptor the connection is created against. */
  integration?: Bdkv2BookDescriptor;
  /** `useConnectionActions().createOAuthAuthorizationConnection`. */
  createOAuthAuthorizationConnection: (
    integration: Bdkv2BookDescriptor,
    authMethodId: string,
    connectionName: string,
    credentials: Record<string, string>,
    labels: string[],
    configMap?: Record<string, Bdkv2Value>,
  ) => Promise<string>;
}

/**
 * Starts an OAuth connection from inside a trigger dialog, recording the
 * dialog's intent first so {@link useResumeTriggerConnection} can reopen it.
 *
 * @param params - Trigger kind, integration, and the connection creator
 * @returns The `onOAuthClick` handler for `AddConnectionDialog`
 */
export function useTriggerOAuthConnection({
  triggerKind,
  integration,
  createOAuthAuthorizationConnection,
}: IUseTriggerOAuthConnectionParams): TTriggerOAuthClick {
  const { pathname } = useLocation();

  return useCallback(
    async (authMethodId, connectionName, credentials, labels, configMap) => {
      // Without a book there is nothing to reopen the dialog on, so the
      // sign-in proceeds unresumable rather than storing an unusable intent.
      if (integration?.name) {
        writeTriggerConnectionIntent({
          kind: triggerKind,
          pathname,
          integrationName: integration.name,
          integrationVersion: integration.version,
        });
      }

      try {
        const authorizeUrl = await createOAuthAuthorizationConnection(
          integration ?? {},
          authMethodId,
          connectionName,
          credentials,
          labels,
          configMap,
        );

        validateOAuthRedirectUrl(authorizeUrl);
        window.location.assign(authorizeUrl);
      } catch (error) {
        clearTriggerConnectionIntent();
        captureException(
          error instanceof Error ? error : new Error(String(error)),
          {
            tags: {
              component: 'TriggerConfigureStep',
              triggerKind,
              action: 'createOAuthAuthorizationConnection',
            },
          },
        );
        throw error;
      }
    },
    [triggerKind, pathname, integration, createOAuthAuthorizationConnection],
  );
}

/**
 * Records the trigger dialog's intent before a managed-credential sign-in
 * leaves the page, so {@link useResumeTriggerConnection} reopens the dialog
 * on return. The connection itself is created by the return handler.
 *
 * @param params - Trigger kind and the integration being connected
 * @returns The handler for `AddConnectionDialog`'s `onManagedSignInRedirect`
 */
export function useRememberTriggerForResume({
  triggerKind,
  integration,
}: Pick<IUseTriggerOAuthConnectionParams, 'triggerKind' | 'integration'>) {
  const { pathname } = useLocation();

  return useCallback(() => {
    if (!integration?.name) return;
    writeTriggerConnectionIntent({
      kind: triggerKind,
      pathname,
      integrationName: integration.name,
      integrationVersion: integration.version,
    });
  }, [triggerKind, pathname, integration]);
}
